/**
 * PolicyDslTab — raw JSON editor for the policy document.
 *
 * The textarea is validated on save against `policyDocumentSchema` (zod).
 * We intentionally avoid a heavy editor (Monaco/CodeMirror) for the MVP:
 *   - keeps the bundle lean on the admin-only path
 *   - parity with the backend DSL is enforced by Zod, not by syntax colour
 *   - once ADR-103 backend ships, we can swap to Monaco behind this tab
 */
import { CheckCircle, FloppyDisk, XCircle } from '@phosphor-icons/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../shared/ui/Button';
import { Card, CardBody, CardHeader } from '../../../shared/ui/Card';
import { FieldError } from '../../../shared/ui/FieldError';
import { useUpdatePolicyDocument } from '../../../features/business-policies/useBusinessPolicies';
import {
  policyDocumentSchema,
  type PolicyRecord,
} from '../../../features/business-policies/types';

export function PolicyDslTab({
  record,
}: {
  record: PolicyRecord;
}): JSX.Element {
  const { t } = useTranslation('adminPolicies');
  const [raw, setRaw] = useState(() =>
    JSON.stringify(record.document, null, 2),
  );
  const [parseError, setParseError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const updateMutation = useUpdatePolicyDocument(record.policyId);

  const handleReset = () => {
    setRaw(JSON.stringify(record.document, null, 2));
    setParseError(null);
    setSaved(false);
  };

  const handleSave = async () => {
    setSaved(false);
    try {
      const parsed = JSON.parse(raw);
      const validated = policyDocumentSchema.safeParse(parsed);
      if (!validated.success) {
        const first = validated.error.issues[0];
        const path = first?.path.join('.') || 'root';
        setParseError(`${path}: ${first?.message ?? t('dsl.invalid')}`);
        return;
      }
      setParseError(null);
      await updateMutation.mutateAsync(validated.data);
      setSaved(true);
    } catch (e) {
      setParseError(
        e instanceof Error ? e.message : t('dsl.invalidJson'),
      );
    }
  };

  return (
    <Card>
      <CardHeader>
        <div>
          <h3 className="biz-heading font-semibold">{t('dsl.title')}</h3>
          <p className="text-biz-sm text-biz-muted-foreground">
            {t('dsl.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-biz-content-lg">
          <Button
            type="button"
            intent="secondary"
            onClick={handleReset}
            disabled={updateMutation.isPending}
          >
            {t('dsl.reset')}
          </Button>
          <Button
            type="button"
            intent="primary"
            leftIcon={<FloppyDisk size={18} weight="regular" />}
            onClick={handleSave}
            loading={updateMutation.isPending}
          >
            {t('dsl.save')}
          </Button>
        </div>
      </CardHeader>
      <CardBody className="space-y-biz-content-lg">
        <textarea
          value={raw}
          onChange={(e) => {
            setRaw(e.target.value);
            setParseError(null);
            setSaved(false);
          }}
          spellCheck={false}
          rows={24}
          className="w-full font-mono text-biz-sm p-biz-content-lg bg-biz-background border border-biz-input rounded-biz-input focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring leading-relaxed"
          aria-label={t('dsl.editorLabel')}
          aria-invalid={Boolean(parseError)}
        />
        {parseError ? (
          <FieldError id="policy-dsl-error">
            <span className="inline-flex items-center gap-1">
              <XCircle size={16} weight="regular" aria-hidden="true" />
              {parseError}
            </span>
          </FieldError>
        ) : saved ? (
          <p className="inline-flex items-center gap-1 text-biz-success text-biz-sm">
            <CheckCircle size={16} weight="regular" aria-hidden="true" />
            {t('dsl.saved')}
          </p>
        ) : (
          <p className="text-biz-xs text-biz-muted-foreground">{t('dsl.hint')}</p>
        )}
      </CardBody>
    </Card>
  );
}
